// src/components/SearchResults.js - with improved progress display only
import React, { useState, useEffect } from 'react';
import ResultItem from './ResultItem';
import ClearResults from './ClearResults';
import CaseReportButton from './CaseReportButton';
import { useTranslation } from '../hooks/useTranslation';
import { useAuth } from '../contexts/AuthContext';
import { getCaseBySearchId, getReviewStatusesForCase } from '../services/caseService';

const SearchResults = ({ 
  results, 
  stats, 
  searchTerm, 
  isLoading, 
  isSearchComplete, 
  searchProgress,
  verificationId,
  isVerificationMode, // Keeping for backward compatibility
  searchId,
  displayMode = 'search', // New prop: 'search', 'verification', 'case-management'
  isHistoricalSearch = false, // New prop to handle historical searches
  entityType = 'person' // Add entityType prop with default value
}) => {
  // Get translation function and auth context
  const t = useTranslation();
  const { currentUser, currentOrg } = useAuth();
  
  // State for managing active filter
  const [activeFilter, setActiveFilter] = useState('all');
  
  // State to track whether to include dismissed items
  const [includeDismissed, setIncludeDismissed] = useState(false);
  
  // Filtered results based on active filter
  const [filteredResults, setFilteredResults] = useState([]);
  
  // Case status based on review status of all results
  const [caseStatus, setCaseStatus] = useState('pending');
  
  // State for review status data
  const [reviewStatusMap, setReviewStatusMap] = useState({});
  const [caseData, setCaseData] = useState(null);
  
  // For backward compatibility, if isVerificationMode is true, set displayMode to 'verification'
  useEffect(() => {
    if (isVerificationMode && displayMode === 'search') {
      // This is just for internal state - we don't want to modify the prop directly
      displayMode = 'verification';
    }
  }, [isVerificationMode, displayMode]);
  
  // Load review status when searchId changes
  useEffect(() => {
    const loadReviewStatus = async () => {
      if (!searchId || !currentUser || !currentOrg) {
        setReviewStatusMap({});
        setCaseData(null);
        return;
      }
      
      try {
        console.log('Loading review status for searchId:', searchId);
        
        // Get the case associated with this search
        const caseInfo = await getCaseBySearchId(searchId, currentOrg.id);
        if (caseInfo) {
          setCaseData(caseInfo);
          setCaseStatus(caseInfo.status || 'pending'); // Use case status from database
          const statusMap = await getReviewStatusesForCase(caseInfo.id, currentOrg.id);
          setReviewStatusMap(statusMap);
          console.log('Loaded review status map:', statusMap);
        } else {
          console.log('No case found for searchId:', searchId);
        }
      } catch (error) {
        console.error('Error loading review status:', error);
      }
    };
    
    loadReviewStatus();
  }, [searchId, currentUser, currentOrg]);
  
  // Reset filter when new results come in and merge with review status
  useEffect(() => {
    setActiveFilter('all');
    
    // Merge results with review status data
    if (results && results.length > 0) {
      const mergedResults = results.map(result => ({
        ...result,
        // Merge review status from the review service
        review_status: reviewStatusMap[result.id]?.review_status || 'pending',
        review_notes: reviewStatusMap[result.id]?.review_notes || '',
        review_timestamp: reviewStatusMap[result.id]?.review_timestamp || '',
        reviewer_userID: reviewStatusMap[result.id]?.reviewer_userID || null
      }));
      
      setFilteredResults(mergedResults);
      
      // Case status is now managed from the database, not computed from results
    } else {
      setFilteredResults([]);
    }
  }, [results, reviewStatusMap]);
  
  // Apply filtering when the active filter changes or includeDismissed changes
  useEffect(() => {
    if (!results || results.length === 0) {
      setFilteredResults([]);
      return;
    }
    
    // Create merged results with review status
    const mergedResults = results.map(result => ({
      ...result,
      review_status: reviewStatusMap[result.id]?.review_status || 'pending',
      review_notes: reviewStatusMap[result.id]?.review_notes || '',
      review_timestamp: reviewStatusMap[result.id]?.review_timestamp || '',
      reviewer_userID: reviewStatusMap[result.id]?.reviewer_userID || null
    }));
    
    // First filter by dismissed status if needed
    let filtered = mergedResults;
    if (!includeDismissed) {
      filtered = mergedResults.filter(result => result.review_status !== 'dismissed');
    }
    
    // Then filter by confidence level
    if (activeFilter !== 'all') {
      filtered = filtered.filter(result => {
        switch(activeFilter) {
          case 'very-high':
            return result.confidenceLevel === "Very High";
          case 'high':
            return result.confidenceLevel === "High";
          case 'medium':
            return result.confidenceLevel === "Medium";
          case 'low':
            return result.confidenceLevel === "Low";
          default:
            return true;
        }
      });
    }
    
    setFilteredResults(filtered);
    
    // Debug: check if results have the expected review_status
    console.log('Filtered results:', filtered.map(r => ({
      id: r.id,
      name: r.fullName,
      review_status: r.review_status || 'not-set',
      has_review_status: 'review_status' in r
    })));
  }, [activeFilter, results, includeDismissed, reviewStatusMap]);
  
  // Helper function for filter counts
  const getCountByLevel = (level) => {
    if (!results) return 0;
    
    // Create merged results with review status
    const mergedResults = results.map(result => ({
      ...result,
      review_status: reviewStatusMap[result.id]?.review_status || 'pending'
    }));
    
    // If we're not including dismissed, only count non-dismissed items
    const resultsToCount = !includeDismissed 
      ? mergedResults.filter(r => r.review_status !== 'dismissed') 
      : mergedResults;
      
    return resultsToCount.filter(r => r.confidenceLevel === level).length;
  };
  
  // Get total count of non-dismissed results
  const getNonDismissedCount = () => {
    if (!results) return 0;
    
    // Create merged results with review status
    const mergedResults = results.map(result => ({
      ...result,
      review_status: reviewStatusMap[result.id]?.review_status || 'pending'
    }));
    
    return mergedResults.filter(r => r.review_status !== 'dismissed').length;
  };
  
  // Calculate progress percentage for improved progress bar
  const getProgressPercentage = () => {
    if (!searchProgress || !searchProgress.total) return 0;
    return Math.min(100, (searchProgress.processed / searchProgress.total) * 100);
  };
  
  // Format number with commas
  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };
  
  // Get case status class
  const getCaseStatusClass = () => {
    switch(caseStatus) {
      case 'completed': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800/50';
      case 'partially-reviewed': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800/50';
      default: return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800/50';
    }
  };
  
  // Get case status text
  const getCaseStatusText = () => {
    switch(caseStatus) {
      case 'completed': return t('search.caseStatusCompleted');
      case 'partially-reviewed': return t('search.caseStatusPartial');
      default: return t('search.caseStatusPending');
    }
  };
  
  // Always show loading state when either:
  // 1. We're explicitly in loading state OR
  // 2. We have a search term but search is not complete (initial data fetching)
  const shouldShowLoading = isLoading || (searchTerm && !isSearchComplete);
  
  // If there's no search performed yet and we're not loading, don't render anything
  if (!searchTerm && !isSearchComplete && !isLoading) {
    return null;
  }
  
  // Handle loading state with improved progress bar
  if (shouldShowLoading) {
    // For historical searches, show a small loading spinner instead of progress bar
    if (isHistoricalSearch) {
      return (
        <div className="flex items-center justify-center py-4">
          <div className="h-5 w-5 rounded-full border-t-2 border-b-2 border-primary animate-spin"></div>
          <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">{t('search.loading')}</span>
        </div>
      );
    }
    
    // Get current stage description text
    const getStageDescription = () => {
      if (!searchProgress || !searchProgress.stage) return t('results.searching');
      
      if (searchProgress.stage === 'first') {
        return t('results.searchingFirstStage');
      } else if (searchProgress.stage === 'vector') {
        return t('results.searchingVectorStage');
      } else if (searchProgress.stage === 'preparing') {
        return t('results.preparingAnalysis');
      } else if (searchProgress.stage === 'second') {
        return t('results.searchingSecondStage');
      }
      
      return t('results.searching');
    };
    
    // Calculate combined progress percentage for both stages
    const getStageProgress = () => {
      if (!searchProgress || !searchProgress.total) return 0;
      
      // If no stage is defined, we're at the very beginning of a new search
      // Show 0% to indicate the search is starting fresh
      if (!searchProgress.stage) {
        return 0;
      }
      
      // Ensure we always show some initial progress to the user
      if (searchProgress.stage === 'first') {
        // First stage represents 40% of total progress
        // Ensure the progress bar always shows at least 5% to provide visual feedback
        const firstStagePercent = (searchProgress.processed / searchProgress.total) * 40;
        return Math.max(5, firstStagePercent);
      } else if (searchProgress.stage === 'vector') {
        // Vector stage represents 40% of total progress
        // Ensure the progress bar always shows at least 5% to provide visual feedback
        const vectorStagePercent = (searchProgress.processed / searchProgress.total) * 40;
        return Math.max(5, vectorStagePercent);
      } else if (searchProgress.stage === 'preparing') {
        // Transition stage - show 40% (completed first stage) with a small pulse effect
        return 40;
      } else if (searchProgress.stage === 'second') {
        // Second stage starts at 40% and represents the remaining 60%
        return 40 + Math.min(60, (searchProgress.processed / searchProgress.total) * 60);
      }
      
      // Default to a small non-zero value to show something is happening
      return 5;
    };
    
    // Calculate progress percentage
    const progressPercentage = getStageProgress();
    
    // For regular searches, show the unified progress bar
    return (
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="flex flex-col items-center justify-center text-gray-600 dark:text-gray-300 py-8">
          <h3 className="text-lg font-medium mb-4">{t('results.searchingTitle')}</h3>
          {isLoading && (
            <div className="w-full max-w-md my-6">
              <p className="text-gray-600 dark:text-gray-400 mb-2 text-sm text-center">
                {getStageDescription()}
              </p>
              <div className="relative pt-1">
                <div className="overflow-hidden h-3 mb-1 text-xs flex rounded-full bg-blue-200 dark:bg-blue-900">
                  <div
                    style={{ width: `${progressPercentage}%` }}
                    className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500 dark:bg-blue-400 transition-all duration-300 ${
                      searchProgress?.stage === 'preparing' ? 'animate-pulse' : ''
                    }`}
                  ></div>
                </div>
                <div className="flex justify-end">
                  <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                    {Math.round(progressPercentage)}%
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Handle review status changes from ResultItem components
  const handleReviewStatusChange = (resultId, prevStatus, newStatus) => {
    // Update the review status map immediately for UI responsiveness
    setReviewStatusMap(prev => ({
      ...prev,
      [resultId]: {
        ...prev[resultId],
        review_status: newStatus,
        review_timestamp: new Date().toISOString(),
        reviewer_userID: currentUser?.uid
      }
    }));
    
    // The case status will be updated automatically by the case service
    // when updateResultReviewStatus is called in ResultItem
    // We'll refresh the case status when the review status map changes
    if (caseData) {
      // Refresh case data to get updated status from database
      setTimeout(async () => {
        try {
          const updatedCase = await getCaseBySearchId(searchId, currentOrg.id);
          if (updatedCase) {
            setCaseStatus(updatedCase.status || 'pending');
          }
        } catch (error) {
          console.error('Error refreshing case status:', error);
        }
      }, 1000); // Small delay to allow case service to update
    }
      
      // Note: Filtering will be automatically handled by the useEffect that watches reviewStatusMap
      // We don't need to manually update filteredResults here since the useEffect will rerun
      // when reviewStatusMap changes and handle all the filtering logic
  };
  
  // Only show results when search is complete and we're not loading
  if (isSearchComplete && !isLoading) {
    // Handle no results - use our ClearResults component
    if (!results || results.length === 0) {
      return <ClearResults searchTerm={searchTerm} stats={stats} searchId={searchId} entityType={entityType} />;
    }
    
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 shadow-sm ${displayMode !== 'search' ? 'border-t-2 border-blue-500 dark:border-blue-600' : ''}`}>
        {/* Results summary */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">{t('search.searchResults')}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('search.foundMatches', { count: results.length, term: searchTerm })}
          </p>
          
          {/* Display case status if we have a searchId */}
          {searchId && (
            <div className="flex items-center gap-3 mt-3">
              <div className={`inline-block px-3 py-1 rounded-full text-sm border ${getCaseStatusClass()}`}>
                <span className="font-medium">{t('search.caseStatus')}:</span> {getCaseStatusText()}
              </div>
              
              {/* Add case report button */}
              <CaseReportButton caseId={searchId} caseStatus={caseStatus} />
            </div>
          )}
          
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
            {t('search.showingTop', {
              shown: filteredResults.length,
              total: includeDismissed ? results.length : getNonDismissedCount()
            })}
          </p>
        </div>
        
        {/* Filter controls with include dismissed checkbox */}
        <div className="mb-6 flex justify-between items-center">
          <div className="flex flex-wrap gap-2">
            <button 
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                activeFilter === 'all' 
                  ? 'bg-blue-600 text-white dark:bg-blue-700' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
              onClick={() => setActiveFilter('all')}
            >
              {t('search.filters.all')} ({includeDismissed ? results.length : getNonDismissedCount()})
            </button>
            <button 
              className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1 ${
                activeFilter === 'very-high'
                  ? 'bg-red-600 text-white dark:bg-red-700'
                  : 'bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50'
              } ${getCountByLevel("Very High") === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => setActiveFilter('very-high')}
              disabled={getCountByLevel("Very High") === 0}
            >
              <i className="fa-solid fa-circle-exclamation"></i> {t('search.confidence.veryHigh')} ({getCountByLevel("Very High")})
            </button>
            <button 
              className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1 ${
                activeFilter === 'high'
                  ? 'bg-yellow-500 text-white dark:bg-yellow-600'
                  : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400 dark:hover:bg-yellow-900/50'
              } ${getCountByLevel("High") === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => setActiveFilter('high')}
              disabled={getCountByLevel("High") === 0}
            >
              <i className="fa-solid fa-triangle-exclamation"></i> {t('search.confidence.high')} ({getCountByLevel("High")})
            </button>
            <button 
              className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1 ${
                activeFilter === 'medium'
                  ? 'bg-blue-600 text-white dark:bg-blue-700'
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50'
              } ${getCountByLevel("Medium") === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => setActiveFilter('medium')}
              disabled={getCountByLevel("Medium") === 0}
            >
              <i className="fa-solid fa-circle-info"></i> {t('search.confidence.medium')} ({getCountByLevel("Medium")})
            </button>
            <button 
              className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1 ${
                activeFilter === 'low'
                  ? 'bg-gray-600 text-white dark:bg-gray-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
              } ${getCountByLevel("Low") === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => setActiveFilter('low')}
              disabled={getCountByLevel("Low") === 0}
            >
              <i className="fa-solid fa-info"></i> {t('search.confidence.low')} ({getCountByLevel("Low")})
            </button>
          </div>
          
          <div className="flex items-center">
            <label className="flex items-center text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
              <input 
                type="checkbox" 
                checked={includeDismissed} 
                onChange={(e) => setIncludeDismissed(e.target.checked)}
                className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:focus:ring-blue-600 dark:bg-gray-700 mr-2"
              />
              {t('search.includeDismissed')}
            </label>
          </div>
        </div>
        
        {/* Results list */}
        <ul className="space-y-4">
          {console.log('SearchResults rendering with searchId:', searchId)}
          {filteredResults.map((result, index) => (
            <ResultItem 
              key={`${result.id}_${result.nameIndex || 0}_${index}`}
              result={result}
              searchTerm={searchTerm}
              t={t}
              searchId={searchId}
              onReviewStatusChange={handleReviewStatusChange}
            />
          ))}
        </ul>
        
        {/* Warning if results limited */}
        {stats && stats.totalMatches > results.length && (
          <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-600 text-yellow-700 dark:text-yellow-400 flex items-start">
            <i className="fa fa-info-circle text-lg mt-0.5 mr-3"></i>
            <p>{t('search.showingTop', { shown: results.length, total: stats.totalMatches })}</p>
          </div>
        )}
      </div>
    );
  }
  
  // Fallback loading state (in case we somehow got here)
  return (
    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="flex flex-col items-center justify-center text-gray-600 dark:text-gray-300 py-8">
        <h3 className="text-lg font-medium">{t('search.initializing')}</h3>
      </div>
    </div>
  );
};

export default SearchResults;